import { resetALL } from "../../../../../../../.globals/redux/actions";
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { clientMutation, clientQuery } from '../../../../../../../core/data/GraphQLApi';
import { endpoints } from '../../../../../../../core/defaultValues';

const INIT_STATE = {
    loadingUpdate: false,
    update: null,
    error: null,
    riskEvents: [],
    loadingRiskEvents: false,
    nextToken: null,
    totalRiskEvents: 0,
    profiles: [],
    loadingProfiles: false,
    profileId: null,
};

export const searchAssetProfiles = createAsyncThunk(
    'interventionRedux/searchAssetProfiles',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await clientQuery(
                `
                query searchAssetProfiles(
                    $conditions: [AssetProfileSearchInput]
                    $sort: [AssetProfileSortInput]
                    $limit: Int
                    $nextToken: String
                ) {
                    data: searchAssetProfiles(
                        conditions: $conditions
                        sort: $sort
                        limit: $limit
                        nextToken: $nextToken
                    ) {
                        items {
                            id
                            name
                        }
                        total
                        nextToken
                    }
                }`,
                {
                    conditions: [],
                    sort: [],
                    limit: 100,
                    nextToken: null,
                },
                endpoints.GRAPHQL_GENERAL_V2
            );
            return response?.data;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch profiles');
        }
    }
);

export const getRiskEvents = createAsyncThunk(
    'interventionRedux/getRiskEvents',
    async (payload, { rejectWithValue, getState }) => {
        try {
            const { profileId, loadMore = false } = payload || {};
            const state = getState();
            const currentNextToken = loadMore ? state.interventionRedux.nextToken : null;

            const response = await clientMutation(
                `
                query searchAssetProfileEvents(
                    $conditions: [AssetProfileEventSearchInput]
                    $sort: [AssetProfileEventSortInput]
                    $limit: Int
                    $nextToken: String
                    $onlyConfigured: Boolean
                ) {
                    searchAssetProfileEvents(
                        conditions: $conditions
                        sort: $sort
                        limit: $limit
                        nextToken: $nextToken
                        onlyConfigured: $onlyConfigured
                    ) {
                        items {
                            id
                            profileId
                            eventId
                            eventName
                            eventType
                            eventCategory
                            status
                            coachable
                            severityParameters
                        }
                        total
                        nextToken
                    }
                }`,
                {
                    conditions: profileId ? [
                        { fields: ["profileId"], terms: [profileId.toString()], exact: true }
                    ] : [],
                    sort: [],
                    limit: 15,
                    nextToken: currentNextToken || '',
                    onlyConfigured: true
                },
                endpoints.GRAPHQL_GENERAL_V2
            );

            if (response.errors && response.errors.length > 0) {
                return rejectWithValue(response.errors[0].message);
            }

            return {
                items: response.searchAssetProfileEvents?.items || [],
                nextToken: response.searchAssetProfileEvents?.nextToken || null,
                total: response.searchAssetProfileEvents?.total || 0,
                loadMore
            };
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch risk events');
        }
    }
);

export const updateInterventionConfig = createAsyncThunk(
    "intervention/updateInterventionConfig",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await clientMutation(
                `
                mutation updateDriverCaseAccountConfig($config: AWSJSON!, $configType: String!) {
                    updateDriverCaseAccountConfig(config: $config, configType: $configType)
                }`,
                {
                    config: JSON.stringify(payload.config),
                    configType: "functions_intervention"
                },
                endpoints.GRAPHQL_GENERAL_V2
            );

            // Check for GraphQL errors in response
            if (response.errors && response.errors.length > 0) {
                return rejectWithValue(response.errors[0].message);
            }

            return { success: response.updateDriverCaseAccountConfig };
        } catch (exc) {
            // Handle network or other errors
            const errorMessage = exc.errors?.[0]?.message || exc.message || 'Unknown error';
            return rejectWithValue(errorMessage);
        }
    }
);

const interventionRedux = createSlice({
    name: "intervention",
    initialState: INIT_STATE,
    reducers: {
        resetIntervention(state, action) {
            return action.payload ? { ...state, ...action.payload } : INIT_STATE;
        },
        setProfileId(state, action) {
            state.profileId = action.payload;
            state.riskEvents = [];
            state.nextToken = null;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetALL, () => INIT_STATE);

        builder.addCase(updateInterventionConfig.pending, (state) => {
            state.loadingUpdate = true;
        })
        builder.addCase(updateInterventionConfig.fulfilled, (state, action) => {
            state.loadingUpdate = false;
            state.update = action.payload;
        })
        builder.addCase(updateInterventionConfig.rejected, (state, action) => {
            state.loadingUpdate = false;
            state.update = { success: false, error: action.payload };
        })
        builder.addCase(getRiskEvents.pending, (state, action) => {
            const { loadMore } = action.meta.arg || {};

            // Reset events when not loading more (new search)
            if (!loadMore) {
                state.riskEvents = [];
                state.nextToken = null;
            }

            state.loadingRiskEvents = true;
        })
        builder.addCase(getRiskEvents.fulfilled, (state, action) => {
            const { items, nextToken, total, loadMore } = action.payload;

            if (loadMore) {
                // Append new items to existing ones
                state.riskEvents = [...state.riskEvents, ...items];
            } else {
                // Replace with new items
                state.riskEvents = items;
            }

            state.nextToken = nextToken;
            state.totalRiskEvents = total;
            state.loadingRiskEvents = false;
        })
        builder.addCase(getRiskEvents.rejected, (state, action) => {
            state.loadingRiskEvents = false;
            state.riskEvents = [];
            state.nextToken = null;
            state.totalRiskEvents = 0;
        })
        builder.addCase(searchAssetProfiles.pending, (state) => {
            state.loadingProfiles = true;
        })
        builder.addCase(searchAssetProfiles.fulfilled, (state, action) => {
            state.loadingProfiles = false;
            state.profiles = action.payload?.items || [];
            state.profileId = action.payload?.items?.[0]?.id || null;
        })
        builder.addCase(searchAssetProfiles.rejected, (state, action) => {
            state.loadingProfiles = false;
            state.profiles = [];
            state.profileId = null;
        })
    },
});

export const { resetIntervention, setProfileId } = interventionRedux.actions;
export default interventionRedux.reducer;
